import { Arrays, Brand, Dates, HolidayAccrualEntry, HolidayAccrualSource, HolidayYear, HolidayYears, Translations } from "@salaxy/core";

import { UiHelpers } from "../../services";

import { ListControllerBase, ListControllerBaseBindings } from "../bases";

/**
 * Controls holiday accrual (lomapäivien kertymä) of the annual leave for a selected holiday period.
 */
export class HolidayYearAccrualController extends ListControllerBase<HolidayYear, HolidayAccrualEntry> {

  /** Bindings for components that use this controller */
  public static bindings = new ListControllerBaseBindings();
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "HolidayYears",
    "UiHelpers",
  ];

  constructor(
    private holidayYears: HolidayYears,
    uiHelpers: UiHelpers,
  ) {
    super(uiHelpers);
  }

  /** List of items */
  public get list(): HolidayAccrualEntry[] {
    if (!this.parent || !this.parent.accrual || !this.parent.accrual.months) {
      return [];
    }
    return this.parent.accrual.months;
  }

  /** Creating of a new item. */
  public getBlank(): HolidayAccrualEntry {
    return {};
  }

  /**
   * Saves changes to the parent object using the CRUD API.
   */
  public saveParent(): void {
    const loader = this.uiHelpers.showLoading("SALAXY.UI_Terms.isSaving");
    this.holidayYears.save(this.parent)
      .then((saved) => {
        this.parent = saved;
        loader.dismiss();
      });
  }

  /** Template for edit UI that is shown in a modal dialog. */
  public getEditDialogTemplateUrl() {
    return "salaxy-components/worker/holidays/HolidayYearAbsencesEditDialog.html";
  }

  /** Chart config for accrual overview. */
  public getChartConfig(list: HolidayAccrualEntry[]) {
    return this.uiHelpers.cache(this, "accrualChart",() => this.createConfig(list));
  }

  private createConfig(list: HolidayAccrualEntry[]): {type: string, data: any, options: any} {
    const data = [[], []];
    const labels = [];
    const startSaldo: number = (this.parent.accrual as any).startSaldo;
    let cumulative = startSaldo;

    for (const entry of list) {
      cumulative += entry.daysAccrued;
      const startOfMonth = Dates.startEnd("today","start-month");
      data[0].push(entry.month <= startOfMonth ? cumulative : null);
      data[1].push(entry.month >= startOfMonth ? cumulative : null);
      labels.push(Translations.get(`SALAXY.ENUM.CalendarMonth.month${Dates.getMonth(entry.month)}.short`));
    }
    return {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: Translations.get("SALAXY.NG1.HolidayYearAccrualComponent.accumulated"),
            data: [
              ...data[0]
            ],
            borderColor: "rgb(159,192,209)",
            fill: {
              target: "origin",
                above: "rgb(234,241,245, 0.5)"
            },
            elements: {
              point: {
                backgroundColor: "rgb(234,241,245)"
              }
            },
          },
          {
            label: Translations.get("SALAXY.NG1.HolidayYearAccrualComponent.futureEstimate"),
            data: [
              ...data[1]
            ],
            fill: {
              target: "origin",
                above: "rgb(248,248,248, 0.5)"
            },
            elements: {
              point: {
                backgroundColor: "rgb(248,248,248)"
              }
            },
          }
        ]
      },
      options: {
        scales: {
          y: {
            ticks: {
              beginAtZero: true,
            },
          },
        },
        plugins: {
          legend: {
            display: false
          }
        }
      },
    };
  }
  /** Gets the color for the row avatar */
  public getRowColor(row: HolidayAccrualEntry | "start" | "total") {
    if (row === "start" || row === "total") {
      return Brand.getBrandColor("primary");
    }
    if (row.month > this.getThisMonth()) {
      return "#ddd";
    }
    if (row.month === this.getThisMonth()) {
      return Brand.getBrandColor("danger");
    }
    return Brand.getBrandColor("primary");
  }

  /** Gets this month: The first date of this month */
  public getThisMonth() {
    return Dates.getToday().substr(0, 8) + "01";
  }

  /**
   * Returns the notes if available or, if not, description text based on the source.
   */
  public getAccrualNotes(row: HolidayAccrualEntry) {
    if (row.notes) {
      return row.notes;
    }
    switch (row.source) {
      case HolidayAccrualSource.Manual:
        return Translations.get("SALAXY.NG1.HolidayYearAccrualComponent.holidayAccrualSource.manual");
      case HolidayAccrualSource.CalcDraft:
        return Translations.get("SALAXY.NG1.HolidayYearAccrualComponent.holidayAccrualSource.calcDraft");
      case HolidayAccrualSource.CalcPaid:
        return Translations.get("SALAXY.NG1.HolidayYearAccrualComponent.holidayAccrualSource.calcPaid");
      case HolidayAccrualSource.Initial:
      default:
        if (row.month === this.getThisMonth()) {
          return Translations.get("SALAXY.NG1.HolidayYearAccrualComponent.holidayAccrualSource.defaultThisMonth");
        }
        if (row.month > this.getThisMonth()) {
          return Translations.get("SALAXY.NG1.HolidayYearAccrualComponent.holidayAccrualSource.defaultEstimate");
        }
        return Translations.get("SALAXY.NG1.HolidayYearAccrualComponent.holidayAccrualSource.default");
    }
  }

  /** Gets calculations related to holiday accruals */
  public getAccrualCalculations() {
    const startSaldo: number = (this.parent.accrual as any).startSaldo;
    const result = {
      accrual: Arrays.sum(this.list, (x) => x.daysAccrued),
      accrualToday: Arrays.sum(this.list.filter((x) => x.month < Dates.getToday()), (x) => x.daysAccrued),
      total: 0,
      totalToday: 0,
    };
    result.total = result.accrual + startSaldo;
    result.totalToday = result.accrualToday + startSaldo;
    return result;
  }
}
